import { ErrorCode } from '@sb-protogen/external/external_control';
import { TCErrorCode } from '@sb-protogen/messenger/trajectory_controller_service';

function assertNeverTcErrorCode(value: never): never {
  throw new Error(`Unmapped TCErrorCode: ${String(value)}`);
}

/**
 * `TCErrorCode` → `ErrorCode`, total over the generated enum.
 *
 * The *channel* decides severity and clearability, and there are two mappers for
 * that (`mapTcFault`, `mapTcRejection`). The code-to-code translation is the
 * same either way, so it lives here once: two copies is how the two mappers get
 * to disagree about what `TC_ERROR_OVERRUN` means.
 *
 * Exhaustive with a `never` check — adding a `TCErrorCode` fails the type-check
 * rather than silently falling through at runtime.
 */
export function tcErrorCode(code: TCErrorCode): ErrorCode {
  switch (code) {
    // --- Reachable on the fault path -------------------------------------
    case TCErrorCode.TC_ERROR_IN_SINGULARITY:
      return ErrorCode.IN_SINGULARITY;
    case TCErrorCode.TC_ERROR_TRAJECTORY_OVER_LIMITS:
      return ErrorCode.TRAJECTORY_OVER_LIMITS;
    case TCErrorCode.TC_ERROR_JOINT_COMMAND_OUT_OF_LIMITS:
      return ErrorCode.JOINT_COMMAND_OUT_OF_LIMITS;
    case TCErrorCode.TC_ERROR_STALENESS_TIMEOUT:
      return ErrorCode.STALENESS_TIMEOUT;
    case TCErrorCode.TC_ERROR_INVALID_TRAJECTORY:
      return ErrorCode.INVALID_TRAJECTORY;
    case TCErrorCode.TC_ERROR_MALFORMED_TARGET:
      return ErrorCode.MALFORMED_TARGET;
    case TCErrorCode.TC_ERROR_COMPUTATION:
      return ErrorCode.COMPUTATION;
    case TCErrorCode.TC_ERROR_WORKSPACE_LIMIT_VIOLATION:
      return ErrorCode.WORKSPACE_LIMIT_VIOLATION;
    case TCErrorCode.TC_ERROR_OVERRUN:
      return ErrorCode.OVERRUN;
    case TCErrorCode.TC_ERROR_STREAM_JUMP:
      return ErrorCode.STREAM_JUMP;
    case TCErrorCode.TC_ERROR_TRACKER_ERROR_EXCEEDED:
      return ErrorCode.TRACKING_ERROR_EXCEEDED;
    case TCErrorCode.TC_ERROR_SELF_COLLISION:
      return ErrorCode.ROBOT_COLLISION_STOP;
    case TCErrorCode.TC_ERROR_INVALID_COLLISION_CONFIG:
      return ErrorCode.INVALID_ARGUMENT;

    // TC_ERROR_NOT_READY carries three unrelated meanings in the controller:
    //
    //   1. PrimitiveError::NOT_IMPLEMENTED on the fault path
    //   2. EnqueueError::NOT_INITIALIZED at enqueue
    //   3. the control loop not answering within ENQUEUE_TIMEOUT
    //
    // (1) and (2) are both "the arm is not ready to do this", which is what
    // ARM_NOT_READY says.
    //
    // (3) is a different statement — "we could not ask" — but **the wire cannot
    // carry the difference**. TrajectoryControllerMessenger sets
    // TC_ERROR_NOT_READY for both the NOT_INITIALIZED enqueue rejection and the
    // ENQUEUE_TIMEOUT branch, with nothing to tell them apart, so this mapping
    // reports a control loop that is not running as ARM_NOT_READY. That is
    // understated rather than wrong (the arm genuinely is not ready), and the
    // catalog message is deliberately generic enough to stay true either way.
    //
    // CONTROLLER_UNAVAILABLE is reserved for the cases where the caller knows
    // independently of this code that it could not reach the controller: the RPC
    // itself failing or never answering (no reply, so no TCErrorCode at all), and
    // ECB's staleness timeout. It is never derived from this enum.
    //
    // Fixing (3) properly needs a distinct wire code, which belongs with the
    // wider command-path work rather than with this mapping.
    case TCErrorCode.TC_ERROR_NOT_READY:
      return ErrorCode.ARM_NOT_READY;

    // --- Command path only (synchronous RPC replies) ----------------------
    case TCErrorCode.TC_ERROR_QUEUE_FULL:
      return ErrorCode.QUEUE_FULL;
    case TCErrorCode.TC_ERROR_NO_CONTROL:
      return ErrorCode.NO_CONTROL;
    case TCErrorCode.TC_ERROR_NOT_IDLE:
      return ErrorCode.NOT_IDLE;
    case TCErrorCode.TC_ERROR_INVALID_ARGUMENT:
      return ErrorCode.INVALID_ARGUMENT;
    case TCErrorCode.TC_ERROR_FRAME_NOT_SPECIFIED:
      return ErrorCode.FRAME_NOT_SPECIFIED;
    case TCErrorCode.TC_ERROR_FRAME_NOT_SUPPORTED:
      return ErrorCode.FRAME_NOT_SUPPORTED;
    case TCErrorCode.TC_ERROR_INCOMPATIBLE_FRAMES:
      return ErrorCode.INCOMPATIBLE_FRAMES;
    case TCErrorCode.TC_ERROR_NONE:
      return ErrorCode.NO_ERROR;

    case TCErrorCode.UNRECOGNIZED:
      return ErrorCode.ERROR_CODE_UNSPECIFIED;

    default:
      return assertNeverTcErrorCode(code);
  }
}

/**
 * The thirteen codes that can actually reach the fault path.
 *
 * `toProtoError` is the only producer of a topic-side code, and it maps exactly
 * the thirteen `PrimitiveError` values. The other nine appear solely in synchronous
 * RPC replies on the command path.
 */
export const REACHABLE_TC_FAULT_CODES: readonly TCErrorCode[] = [
  TCErrorCode.TC_ERROR_IN_SINGULARITY,
  TCErrorCode.TC_ERROR_TRAJECTORY_OVER_LIMITS,
  TCErrorCode.TC_ERROR_JOINT_COMMAND_OUT_OF_LIMITS,
  TCErrorCode.TC_ERROR_STALENESS_TIMEOUT,
  TCErrorCode.TC_ERROR_INVALID_TRAJECTORY,
  TCErrorCode.TC_ERROR_MALFORMED_TARGET,
  TCErrorCode.TC_ERROR_COMPUTATION,
  TCErrorCode.TC_ERROR_WORKSPACE_LIMIT_VIOLATION,
  TCErrorCode.TC_ERROR_OVERRUN,
  TCErrorCode.TC_ERROR_STREAM_JUMP,
  TCErrorCode.TC_ERROR_TRACKER_ERROR_EXCEEDED,
  TCErrorCode.TC_ERROR_NOT_READY,
  TCErrorCode.TC_ERROR_SELF_COLLISION,
];

/**
 * The nine command-path codes: they appear only in synchronous RPC replies on
 * the command path, never on the fault path. A SpeedJ/SpeedL jog enqueue
 * refused by the controller reaches them through `tcRejectionFault`
 * (`libs/routine-runner/util/tcOperatorFaultMessage.ts`); the rest are defined
 * here so the channel's vocabulary is complete.
 */
export const COMMAND_PATH_ONLY_TC_CODES: readonly TCErrorCode[] = [
  TCErrorCode.TC_ERROR_QUEUE_FULL,
  TCErrorCode.TC_ERROR_NO_CONTROL,
  TCErrorCode.TC_ERROR_NOT_IDLE,
  TCErrorCode.TC_ERROR_INVALID_ARGUMENT,
  TCErrorCode.TC_ERROR_FRAME_NOT_SPECIFIED,
  TCErrorCode.TC_ERROR_FRAME_NOT_SUPPORTED,
  TCErrorCode.TC_ERROR_INCOMPATIBLE_FRAMES,
  TCErrorCode.TC_ERROR_INVALID_COLLISION_CONFIG,
  TCErrorCode.TC_ERROR_NONE,
];
